import type { JSX } from 'react';

import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { MachineAllocation } from '@/types/machines';

/**
 * Plan 11-02 — AllocationHistoryTable
 *
 * Read-only reverse-chronological allocation history. Used by the Detail
 * page's "Allocation History" tab and (Plan 11-03) by the SiteMachinesTab.
 *
 * Active rows (deallocatedAt === null) render an "Active" badge. Notes /
 * deallocation reason are truncated to 50 chars with a tooltip.
 *
 * Phase 11 ships actor display as "User #{id}"; Phase 12 will swap to the
 * resolved fullName once the user-lookup wire is live.
 */

export interface AllocationHistoryTableProps {
  readonly rows: readonly MachineAllocation[];
}

function formatDateTime(iso: string | null): string {
  if (iso === null) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function truncate(s: string | null, n: number): string {
  if (s === null) return '—';
  return s.length <= n ? s : `${s.slice(0, n - 1)}…`;
}

export function AllocationHistoryTable({ rows }: AllocationHistoryTableProps): JSX.Element {
  // Defensive sort — reverse-chronological — even if the wire shape arrives
  // pre-sorted, we don't trust the caller to have done it.
  const sorted = [...rows].sort((a, b) => b.allocatedAt.localeCompare(a.allocatedAt));

  if (sorted.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="alloc-history-empty">
        No allocation history yet.
      </p>
    );
  }

  return (
    <Table data-testid="alloc-history-table">
      <TableHeader>
        <TableRow>
          <TableHead>Site</TableHead>
          <TableHead>Allocated at</TableHead>
          <TableHead>Deallocated at</TableHead>
          <TableHead>Notes / reason</TableHead>
          <TableHead>Actor</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sorted.map((row) => {
          const noteText =
            row.deallocatedAt === null ? row.allocationNotes : row.deallocationReason;
          return (
            <TableRow key={row.id} data-testid={`alloc-history-row-${String(row.id)}`}>
              <TableCell>{row.siteName}</TableCell>
              <TableCell className="font-mono text-xs">{formatDateTime(row.allocatedAt)}</TableCell>
              <TableCell>
                {row.deallocatedAt === null ? (
                  <Badge variant="default">Active</Badge>
                ) : (
                  <span className="font-mono text-xs">{formatDateTime(row.deallocatedAt)}</span>
                )}
              </TableCell>
              <TableCell title={noteText ?? ''}>{truncate(noteText, 50)}</TableCell>
              <TableCell className="font-mono text-xs">
                {row.deallocatedByUserId !== null
                  ? `User #${String(row.deallocatedByUserId)}`
                  : `User #${String(row.allocatedByUserId)}`}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
